import { useMemo, useState, type ReactNode } from "react";
import { CaretDown, CaretRight, PencilSimple } from "@phosphor-icons/react";
import { getProductCategoryPrefixes, matchesProductSearch, type CodeCategoryNode } from "@longjie/shared";
import { ProductSearch } from "./ProductSearch";
import { useProductSearch } from "./useProductSearch";
import { CategoryImage } from "./CategoryImage";
import { categoryLevelStyle } from "./category-level-style";
import "./categories.css";
import "./category-navigation.css";

function indexCategoryChildren(nodes: CodeCategoryNode[]) {
  const children = new Map<string | null, CodeCategoryNode[]>();
  for (const node of nodes) {
    const siblings = children.get(node.parentPrefix) ?? [];
    siblings.push(node);
    children.set(node.parentPrefix, siblings);
  }
  return children;
}

/** A selected parent includes every product beneath that prefix. */
export function CodeCategorySelect({ nodes, value, onChange, legacyCategories = [], allValue = "", allLabel = "全部分类", ariaLabel = "选择商品分类", disabled = false, purpose = "filter" }: {
  nodes: CodeCategoryNode[];
  value: string;
  onChange: (value: string) => void;
  legacyCategories?: Array<{ id: string; name: string; active?: boolean; productCount?: number }>;
  allValue?: string;
  allLabel?: string;
  ariaLabel?: string;
  disabled?: boolean;
  purpose?: "filter" | "create";
}) {
  const selectedPrefix = value.startsWith("code:") ? value.slice(5) : "";
  const children = useMemo(() => indexCategoryChildren(nodes), [nodes]);
  const levels: Array<{ parentPrefix: string | null; options: CodeCategoryNode[]; selected: string }> = [];
  let parentPrefix: string | null = null;
  // Build only the selected branch, so even deep paths stay usable on a phone.
  for (let depth = 1; depth <= nodes.length; depth += 1) {
    const options: CodeCategoryNode[] = children.get(parentPrefix) ?? [];
    if (!options.length) break;
    const selected: string = options.find((node) => selectedPrefix.startsWith(node.prefix))?.prefix ?? "";
    levels.push({ parentPrefix, options, selected });
    if (!selected) break;
    parentPrefix = selected;
  }
  if (!levels.length) levels.push({ parentPrefix: null, options: [], selected: "" });
  const selectedNode = nodes.find((node) => node.prefix === selectedPrefix);
  return <div className="code-category-select">
    <div className="code-category-select__levels">{levels.map((level, index) => <label key={level.parentPrefix ?? "root"}>
      <span>{purpose === "create" && index === 0 ? "已有分类" : `第 ${index + 1} 级分类`}</span>
      <select aria-label={index === 0 ? ariaLabel : `${ariaLabel} · 第 ${index + 1} 级`} value={level.selected} disabled={disabled}
        onChange={(event) => onChange(event.target.value ? `code:${event.target.value}` : level.parentPrefix ? `code:${level.parentPrefix}` : allValue)}>
        <option value="">{index === 0 ? allLabel : purpose === "create" ? "不选下级分类" : "全部下级"}</option>
        {level.options.map((node) => <option key={node.prefix} value={node.prefix}>{node.label}{purpose === "filter" ? `（${node.productCount}）` : ""}</option>)}
      </select>
    </label>)}</div>
    {selectedNode && <p className="code-category-select__path">分类路径：{selectedNode.path}{purpose === "filter" ? " · 包含全部下级商品" : ""}</p>}
    {selectedPrefix && !selectedNode && <div className="code-category-select__missing" role="status">
      <span>当前分类路径 {selectedPrefix.match(/.{2}/g)?.join(" / ")} 已无可用商品。</span>
      <button type="button" disabled={disabled} onClick={() => onChange(allValue)}>{purpose === "create" ? "重新选择分类" : "清除分类筛选"}</button>
    </div>}
    {legacyCategories.length > 0 && <details className="code-category-legacy" open={value !== allValue && !value.startsWith("code:")}>
      <summary>按原分类名称分组筛选</summary>
      <label><span className="sr-only">{ariaLabel} · 原分类名称分组</span><select value={value.startsWith("code:") ? allValue : value}
        aria-label={`${ariaLabel} · 原分类名称分组`} disabled={disabled} onChange={(event) => onChange(event.target.value)}>
        <option value={allValue}>{allLabel}</option>
        {legacyCategories.map((category) => <option key={category.id} value={category.id}>{category.name}{category.active === false ? "（已停用）" : ""}{category.productCount === undefined ? "" : `（${category.productCount}）`}</option>)}
      </select></label>
    </details>}
  </div>;
}

export type CodeCategoryProduct = {
  id: string;
  code: string;
  name: string;
  active: boolean;
  skuSearchFields?: Array<{ skuCode: string; specification: string }>;
  stock: Array<{ skuId: string; skuCode: string; specification: string; unit: string; quantity: number }> | null;
};

export function CodeCategoryTree({ nodes, products, isAdmin = false, onEditCategory, onEditProduct, selectedPrefix, onSelectCategory, showSearch = true, disabled = false, toolbarStart }: {
  nodes: CodeCategoryNode[];
  products?: CodeCategoryProduct[];
  isAdmin?: boolean;
  onEditCategory?: (node: CodeCategoryNode) => void;
  onEditProduct?: (product: CodeCategoryProduct) => void;
  selectedPrefix?: string;
  onSelectCategory?: (node: CodeCategoryNode) => void;
  showSearch?: boolean;
  disabled?: boolean;
  toolbarStart?: ReactNode;
}) {
  const children = useMemo(() => indexCategoryChildren(nodes), [nodes]);
  const roots = children.get(null) ?? [];
  const productsByPrefix = useMemo(() => {
    const index = new Map<string, CodeCategoryProduct[]>();
    const seenProducts = new Set<string>();
    for (const product of products ?? []) {
      if (seenProducts.has(product.id)) continue;
      seenProducts.add(product.id);
      const prefix = getProductCategoryPrefixes(product.code).at(-1);
      if (!prefix) continue;
      const directProducts = index.get(prefix) ?? [];
      directProducts.push(product);
      index.set(prefix, directProducts);
    }
    return index;
  }, [products]);
  const [categoryQuery, setCategoryQuery] = useState("");
  const productSearch = useProductSearch();
  const categoryOnly = products === undefined && Boolean(onSelectCategory);
  const [expanded, setExpanded] = useState(() => {
    const firstRoot = roots[0];
    if (!firstRoot) return new Set<string>();
    const firstProduct = [...(products ?? [])].sort((a, b) => a.code.localeCompare(b.code, "en"))
      .find((product) => getProductCategoryPrefixes(product.code)[0] === firstRoot.prefix);
    return new Set(firstProduct ? getProductCategoryPrefixes(firstProduct.code) : [firstRoot.prefix]);
  });
  const search = (categoryOnly ? categoryQuery : productSearch.query).trim().toLocaleLowerCase("zh-CN");
  const { matchingProductIds, visiblePrefixes, matchingProductCounts } = useMemo(() => {
    const matchingProductIds = new Set<string>();
    const visiblePrefixes = new Set<string>();
    const matchingProductCounts = new Map<string, number>();
    if (!search) return { matchingProductIds, visiblePrefixes, matchingProductCounts };
    const matchingCategoryPrefixes = new Set(nodes.filter((node) => node.prefix.includes(search)
      || node.path.toLocaleLowerCase("zh-CN").includes(search) || node.label.toLocaleLowerCase("zh-CN").includes(search)).map((node) => node.prefix));
    function matchesCategoryPath(prefix: string) {
      for (let end = 2; end <= prefix.length; end += 2) {
        if (matchingCategoryPrefixes.has(prefix.slice(0, end))) return true;
      }
      return false;
    }
    function retainPath(prefix: string, productMatch = false) {
      for (let end = 2; end <= prefix.length; end += 2) {
        const ancestor = prefix.slice(0, end);
        visiblePrefixes.add(ancestor);
        if (productMatch) matchingProductCounts.set(ancestor, (matchingProductCounts.get(ancestor) ?? 0) + 1);
      }
    }
    for (const node of nodes) {
      if (matchesCategoryPath(node.prefix)) retainPath(node.prefix);
    }
    for (const [prefix, directProducts] of productsByPrefix) {
      for (const product of directProducts) {
        const candidate = { productCode: product.code, productName: product.name };
        if (matchesProductSearch(candidate, search)
          || product.skuSearchFields?.some((sku) => matchesProductSearch({ ...candidate, skuCode: sku.skuCode, specification: sku.specification }, search))
          || product.stock?.some((stock) => matchesProductSearch({ ...candidate, skuCode: stock.skuCode, specification: stock.specification }, search))
          || matchesCategoryPath(prefix)) {
          matchingProductIds.add(product.id);
          retainPath(prefix, true);
        }
      }
    }
    return { matchingProductIds, visiblePrefixes, matchingProductCounts };
  }, [nodes, productsByPrefix, search]);

  function toggleNode(prefix: string, forceOpen = false) {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(prefix) && !forceOpen) next.delete(prefix); else next.add(prefix);
      return next;
    });
  }
  function renderProduct(product: CodeCategoryProduct) {
    return <article className="code-category-product" key={product.id} aria-label={`商品 ${product.code} ${product.name}`}>
      <div className="code-category-product__info"><code>{product.code}</code><strong>{product.name}</strong>
        {!product.active && <small>已停用</small>}
        {isAdmin && onEditProduct && <button className="code-category-edit" type="button" disabled={disabled} aria-label={`编辑商品 ${product.code} ${product.name}`}
          onClick={(event) => { event.stopPropagation(); onEditProduct(product); }}>编辑商品</button>}
      </div>
      <div className="code-category-product__stock">{product.stock === null ? <span className="code-category-product__empty">库存未启用</span>
        : product.stock.length === 0 ? <span className="code-category-product__empty">暂无规格库存</span>
          : product.stock.map((stock) => <div className="code-category-stock-line" key={stock.skuId}>
            <span>{stock.specification || stock.skuCode}</span><strong>{stock.quantity.toLocaleString("zh-CN", { maximumFractionDigits: 6 })} <small>{stock.unit}</small></strong>
          </div>)}</div>
    </article>;
  }
  function renderNode(node: CodeCategoryNode) {
    const descendants = (children.get(node.prefix) ?? []).filter((child) => !search || visiblePrefixes.has(child.prefix));
    const hasSubcategories = (children.get(node.prefix)?.length ?? 0) > 0;
    const directProducts = (productsByPrefix.get(node.prefix) ?? []).filter((product) => !search || matchingProductIds.has(product.id));
    const productCount = search && products !== undefined ? matchingProductCounts.get(node.prefix) ?? 0 : node.productCount;
    const isExpanded = search ? visiblePrefixes.has(node.prefix) : expanded.has(node.prefix);
    const categoryPath = <span className="code-category-tree__path" aria-label={`分类路径 ${node.path}`} title={`分类路径 ${node.path}`}>
      {node.prefix.match(/.{2}/g)?.map((segment, index, segments) => <span className="code-category-tree__path-part" style={categoryLevelStyle(index + 1)} key={index}>
        {index > 0 && <span className="code-category-tree__path-separator" aria-hidden="true">/</span>}
        <span className={index === segments.length - 1 ? "code-category-tree__path-current" : undefined}>{segment}</span>
      </span>)}
    </span>;
    const content = <>
      {onSelectCategory ? <>
        <span className="code-category-tree__toggle" aria-hidden="true">{hasSubcategories
          ? isExpanded ? <CaretDown weight="bold" /> : <CaretRight weight="bold" />
          : <span className="code-category-tree__leaf-dot" />}</span>
        <span className="code-category-tree__info">
          <span className="code-category-tree__image"><CategoryImage prefix={node.prefix} imageUrl={node.imageUrl ?? null} alt={`${node.label}分类照片`} /></span>
          <span className="code-category-tree__meta"><span className="code-category-tree__title"><span className="code-category-tree__level" aria-label={`第 ${node.depth} 级分类`}>{node.depth === 1 ? `${node.segment} 大类` : `${node.depth}级`}</span>
              {node.name ? <strong title={node.name}>{node.name}</strong> : categoryPath}</span>
            <span className="code-category-tree__details">
              {node.name && node.depth > 1 && categoryPath}
              <span className="code-category-tree__counts"><span><b>{productCount}</b> 种商品</span>
                {hasSubcategories && <span className="code-category-tree__descendants" title="商品数量包含全部下级分类">含下级</span>}</span>
            </span>
          </span>
        </span>
      </> : <>
        <span className="code-category-tree__info">
          <span className="code-category-tree__image"><CategoryImage prefix={node.prefix} imageUrl={node.imageUrl ?? null} alt={`${node.label}分类照片`} /></span>
          <span className="code-category-tree__meta"><strong>{node.label}</strong>
            <small>第 {node.depth} 级 · 前缀 {node.prefix} · 路径 {node.path}</small></span>
        </span>
        <span className="code-category-tree__counts">共 {productCount} 种商品{hasSubcategories ? "（含下级）" : ""}</span>
      </>}
      {isAdmin && onEditCategory && <button className="code-category-edit" type="button" disabled={disabled} aria-label={`编辑分类 ${node.path}`}
        title={`编辑分类 ${node.path}`} onClick={(event) => { event.preventDefault(); event.stopPropagation(); onEditCategory(node); }}>
        {onSelectCategory ? <PencilSimple weight="bold" aria-hidden="true" /> : "编辑分类"}</button>}
    </>;
    return <li key={node.prefix} data-depth={node.depth} style={onSelectCategory ? categoryLevelStyle(node.depth) : undefined}
      className={selectedPrefix === node.prefix ? "code-category-tree__selected" : undefined}><details open={isExpanded}>
      <summary aria-label={`展开分类 ${node.path}`} aria-disabled={disabled || undefined} aria-current={selectedPrefix === node.prefix ? "true" : undefined}
        onClick={(event) => {
          event.preventDefault();
          if (disabled) return;
          onSelectCategory?.(node);
          if (!search) toggleNode(node.prefix, Boolean(onSelectCategory && selectedPrefix !== node.prefix));
        }}>{content}</summary>
      {isExpanded && directProducts.length > 0 && <div className="code-category-products">
        <div className="code-category-products__heading"><span>商品编码 · 商品名称</span><span>库存数量</span></div>
        {directProducts.map(renderProduct)}
      </div>}
      {isExpanded && !hasSubcategories && directProducts.length === 0 && products === undefined && !onSelectCategory && <p className="code-category-tree__empty">商品明细暂不可用，请刷新。</p>}
      {isExpanded && descendants.length > 0 && <ul>{descendants.map(renderNode)}</ul>}
    </details></li>;
  }
  return <div className="code-category-browser">
    <div className="code-category-tree__toolbar">{showSearch && (categoryOnly
      ? <label><span>查找分类</span><input type="search" disabled={disabled} value={categoryQuery} onChange={(event) => setCategoryQuery(event.target.value)} placeholder="分类编码或分类名称" /></label>
      : <ProductSearch search={productSearch} disabled={disabled} ariaLabel="查找分类或商品" />)}
      <div>{toolbarStart}<button type="button" disabled={disabled || Boolean(search)} onClick={() => setExpanded(new Set(nodes.map((node) => node.prefix)))}>展开全部</button>
        <button type="button" disabled={disabled || Boolean(search)} onClick={() => setExpanded(new Set())}>收起全部</button></div>
    </div>
    {search && visiblePrefixes.size === 0 ? <p className="code-category-tree__empty">没有匹配的分类或商品。</p>
      : <ul className={onSelectCategory ? "code-category-tree code-category-tree--selectable" : "code-category-tree"} aria-label="按商品编码分类树">{roots.filter((node) => !search || visiblePrefixes.has(node.prefix)).map(renderNode)}</ul>}
  </div>;
}

export function ProductCodeCategoryNote({ code }: { code: string }) {
  const normalizedCode = code.trim();
  const prefixes = getProductCategoryPrefixes(normalizedCode);
  if (!prefixes.length) return null;
  const path = prefixes.map((prefix) => prefix.slice(-2)).join(" / ");
  return <div className="product-code-category-note" role="status">
    <strong>按编码分类：{path} · 商品序号 {normalizedCode.slice(-2)}</strong>
    <p>共 {prefixes.length} 级分类；完整商品编码 {normalizedCode} 是商品的唯一编码。</p>
  </div>;
}
